import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";

const BALLOON_COLORS = ["#FF6B6B", "#FFD93D", "#6BCB77", "#4D96FF", "#FF6FC8", "#9B59B6", "#F39C12"];
const EMOJI_SET = ["🎈", "🎉", "🎊", "🎂", "🧁", "🎁", "🥳", "🎀", "🪅", "✨"];

const Balloon = ({ delay, x, color }) => (
  <motion.div
    className="absolute text-4xl pointer-events-none select-none"
    initial={{ y: "110vh", x: `${x}vw`, opacity: 0, scale: 0.5 }}
    animate={{ y: "-20vh", opacity: [0, 1, 1, 0.8, 0], scale: [0.5, 1.2, 1, 1, 0.8] }}
    transition={{ duration: 4 + Math.random() * 2, delay, ease: "easeOut" }}
    style={{ filter: `drop-shadow(0 4px 6px rgba(0,0,0,0.15))` }}
  >
    <svg width="48" height="64" viewBox="0 0 48 64">
      <ellipse cx="24" cy="24" rx="18" ry="22" fill={color} opacity="0.9" />
      <ellipse cx="18" cy="16" rx="5" ry="7" fill="white" opacity="0.3" />
      <path d="M24 46 L22 64 M24 46 L26 64" stroke={color} strokeWidth="1.5" fill="none" opacity="0.6" />
      <polygon points="21,44 27,44 24,50" fill={color} opacity="0.8" />
    </svg>
  </motion.div>
);

const FloatingEmoji = ({ delay, x, emoji }) => (
  <motion.div
    className="absolute text-3xl pointer-events-none select-none"
    initial={{ y: "100vh", x: `${x}vw`, opacity: 0, rotate: -30 }}
    animate={{ y: "-10vh", opacity: [0, 1, 1, 0], rotate: [0, 20, -20, 10] }}
    transition={{ duration: 3.5 + Math.random() * 2, delay, ease: "easeOut" }}
  >
    {emoji}
  </motion.div>
);

const PartyHat = ({ delay, x, rotation }) => (
  <motion.div
    className="absolute pointer-events-none select-none"
    initial={{ y: "105vh", x: `${x}vw`, opacity: 0, rotate: rotation }}
    animate={{ y: "-15vh", opacity: [0, 1, 1, 0.6, 0], rotate: [rotation, rotation + 20, rotation - 15] }}
    transition={{ duration: 4.5, delay, ease: "easeOut" }}
  >
    <svg width="40" height="48" viewBox="0 0 40 48">
      <polygon points="20,2 4,44 36,44" fill="#FFD93D" stroke="#F39C12" strokeWidth="1" />
      <circle cx="20" cy="4" r="4" fill="#FF6B6B" />
      <rect x="8" y="20" width="24" height="3" rx="1" fill="#4D96FF" opacity="0.6" />
      <rect x="12" y="30" width="16" height="3" rx="1" fill="#6BCB77" opacity="0.6" />
    </svg>
  </motion.div>
);

const _confettiTimers = [];

const fireConfetti = () => {
  // Clear any previous timers
  _confettiTimers.forEach(clearTimeout);
  _confettiTimers.length = 0;
  
  const defaults = { startVelocity: 30, spread: 360, ticks: 80, zIndex: 9999 };
  const fire = (opts) => confetti({ ...defaults, ...opts });

  fire({ particleCount: 80, origin: { x: 0.5, y: 0.4 }, colors: BALLOON_COLORS });

  _confettiTimers.push(setTimeout(() => fire({ particleCount: 50, angle: 60, spread: 55, origin: { x: 0, y: 0.6 }, colors: BALLOON_COLORS }), 300));
  _confettiTimers.push(setTimeout(() => fire({ particleCount: 50, angle: 120, spread: 55, origin: { x: 1, y: 0.6 }, colors: BALLOON_COLORS }), 500));
  _confettiTimers.push(setTimeout(() => fire({ particleCount: 30, origin: { x: 0.5, y: 0.3 }, colors: ["#FFD700", "#FFA500", "#FF6347"], shapes: ["star"], scalar: 1.5 }), 800));
  _confettiTimers.push(setTimeout(() => {
    fire({ particleCount: 60, angle: 90, spread: 120, origin: { x: 0.3, y: 0 }, colors: BALLOON_COLORS, gravity: 1.2 });
    fire({ particleCount: 60, angle: 90, spread: 120, origin: { x: 0.7, y: 0 }, colors: BALLOON_COLORS, gravity: 1.2 });
  }, 1200));
  _confettiTimers.push(setTimeout(() => fire({ particleCount: 40, origin: { x: 0.2, y: 0.5 }, colors: BALLOON_COLORS, spread: 80 }), 3000));
  _confettiTimers.push(setTimeout(() => fire({ particleCount: 40, origin: { x: 0.8, y: 0.5 }, colors: BALLOON_COLORS, spread: 80 }), 4500));
  _confettiTimers.push(setTimeout(() => fire({ particleCount: 50, origin: { x: 0.5, y: 0.3 }, colors: ["#FFD700", "#FFA500", "#FF6347"], shapes: ["star"], scalar: 1.5 }), 6000));
  _confettiTimers.push(setTimeout(() => {
    fire({ particleCount: 40, angle: 60, spread: 55, origin: { x: 0, y: 0.4 }, colors: BALLOON_COLORS });
    fire({ particleCount: 40, angle: 120, spread: 55, origin: { x: 1, y: 0.4 }, colors: BALLOON_COLORS });
  }, 8000));
  _confettiTimers.push(setTimeout(() => fire({ particleCount: 80, origin: { x: 0.5, y: 0.4 }, colors: BALLOON_COLORS, spread: 360, startVelocity: 40 }), 10000));
};

export const BirthdayCelebration = ({ name }) => {
  const [show, setShow] = useState(true);
  const fired = useRef(false);

  useEffect(() => {
    if (!fired.current) {
      fired.current = true;
      fireConfetti();
    }
    const t = setTimeout(() => setShow(false), 12000);
    return () => {
      clearTimeout(t);
      _confettiTimers.forEach(clearTimeout);
      _confettiTimers.length = 0;
    };
  }, []);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed inset-0 z-[9998] flex items-center justify-center pointer-events-none"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8 }}
          data-testid="birthday-celebration"
        >
          {/* Dark overlay */}
          <motion.div
            className="absolute inset-0 bg-black/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.5, 0.4] }}
            transition={{ duration: 0.5 }}
          />

          {/* Balloons */}
          {Array.from({ length: 10 }).map((_, i) => (
            <Balloon key={`b${i}`} delay={0.2 + i * 0.25} x={5 + (i * 10) % 90} color={BALLOON_COLORS[i % BALLOON_COLORS.length]} />
          ))}

          {/* Floating emojis */}
          {Array.from({ length: 8 }).map((_, i) => (
            <FloatingEmoji key={`e${i}`} delay={0.5 + i * 0.3} x={8 + (i * 13) % 85} emoji={EMOJI_SET[i % EMOJI_SET.length]} />
          ))}

          {/* Party hats */}
          {Array.from({ length: 4 }).map((_, i) => (
            <PartyHat key={`h${i}`} delay={0.8 + i * 0.4} x={15 + i * 22} rotation={-15 + i * 10} />
          ))}

          {/* Center message */}
          <motion.div
            className="relative z-10 text-center px-6"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: [0, 1.3, 1], opacity: 1 }}
            transition={{ type: "spring", damping: 8, delay: 0.3 }}
          >
            <motion.div
              className="text-6xl mb-3"
              animate={{ rotate: [0, -10, 10, -10, 10, 0] }}
              transition={{ duration: 1, delay: 0.5 }}
            >
              🎂
            </motion.div>
            <motion.h1
              className="text-4xl font-black text-white drop-shadow-lg"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              Happy Birthday{name ? `, ${name}` : ""}!
            </motion.h1>
            <motion.p
              className="text-lg text-white/90 font-medium mt-2 drop-shadow"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
            >
              Time to claim your birthday deals!
            </motion.p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
